/**
 * Permission Utility Functions
 * Helper functions to check user permissions and roles
 */

import { ROLES, PERMISSIONS, Role, Permission } from 'constants/permissions';
import { User } from 'models/user.model';

/**
 * Get all permission names from user's roles
 * Flattens the nested roles[].permissions[] structure
 */
export const getAllPermissions = (user: User | null | undefined): string[] => {
  if (!user || !user.roles || user.roles.length === 0) return [];

  const permissionSet = new Set<string>();

  user.roles.forEach((role) => {
    role.permissions.forEach((permission) => {
      permissionSet.add(permission.name);
    });
  });

  return Array.from(permissionSet);
};

/**
 * Get all role names from user
 */
export const getAllRoles = (user: User | null | undefined): string[] => {
  if (!user || !user.roles || user.roles.length === 0) return [];
  return user.roles.map((role) => role.name);
};

/**
 * Check if user has a specific permission
 */
export const hasPermission = (user: User | null | undefined, permission: Permission): boolean => {
  if (!user) return false;

  // Admin (and ProductManager) roles have all permissions
  if (hasAnyRole(user, [ROLES.ADMIN, ROLES.PRODUCT_MANAGER])) return true;

  // Check if user has the specific permission
  const userPermissions = getAllPermissions(user);
  return userPermissions.includes(permission);
};

/**
 * Check if user has ANY of the specified permissions
 */
export const hasAnyPermission = (user: User | null | undefined, permissions: Permission[]): boolean => {
  if (!user || !permissions || permissions.length === 0) return false;
  return permissions.some((permission) => hasPermission(user, permission));
};

/**
 * Check if user has ALL of the specified permissions
 */
export const hasAllPermissions = (user: User | null | undefined, permissions: Permission[]): boolean => {
  if (!user || !permissions || permissions.length === 0) return false;
  return permissions.every((permission) => hasPermission(user, permission));
};

/**
 * Check if user has a specific role
 */
export const hasRole = (user: User | null | undefined, role: Role): boolean => {
  if (!user) return false;
  const userRoles = getAllRoles(user);
  return userRoles.includes(role);
};

/**
 * Check if user has ANY of the specified roles
 */
export const hasAnyRole = (user: User | null | undefined, roles: Role[]): boolean => {
  if (!user || !roles || roles.length === 0) return false;
  const userRoles = getAllRoles(user);
  return roles.some((role) => userRoles.includes(role));
};

/**
 * Check if user is admin
 */
export const isAdmin = (user: User | null | undefined): boolean => {
  return hasRole(user, ROLES.ADMIN);
};

/**
 * Check if user is support
 */
export const isSupport = (user: User | null | undefined): boolean => {
  return hasRole(user, ROLES.SUPPORT);
};

/**
 * Check if user is product manager
 */
export const isProductManager = (user: User | null | undefined): boolean => {
  return hasRole(user, ROLES.PRODUCT_MANAGER);
};

/**
 * Get primary role display name (translation key)
 * Returns the first role's name
 */
export const getRoleDisplayName = (user: User | null | undefined): string => {
  if (!user || !user.roles || user.roles.length === 0) return 'roles.unknown';

  const primaryRole = user.roles[0].name;

  const roleMap: Record<string, string> = {
    [ROLES.ADMIN]: 'roles.admin',
    [ROLES.PRODUCT_MANAGER]: 'roles.productManager',
    [ROLES.SUPPORT]: 'roles.support',
    [ROLES.USER]: 'roles.user',
    [ROLES.GUEST]: 'roles.guest'
  };

  return roleMap[primaryRole] || 'roles.unknown';
};

/**
 * Check if user can manage products
 */
export const canManageProducts = (user: User | null | undefined): boolean => {
  return hasAnyPermission(user, [PERMISSIONS.PRODUCTS_CREATE, PERMISSIONS.PRODUCTS_EDIT, PERMISSIONS.PRODUCTS_DELETE]);
};

/**
 * Check if user can manage orders
 */
export const canManageOrders = (user: User | null | undefined): boolean => {
  return hasPermission(user, PERMISSIONS.ORDERS_MANAGE);
};

/**
 * Check if user can manage users
 */
export const canManageUsers = (user: User | null | undefined): boolean => {
  return hasAnyPermission(user, [PERMISSIONS.USERS_CREATE, PERMISSIONS.USERS_EDIT, PERMISSIONS.USERS_DELETE]);
};
